import React, { useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import GenericModal from '@components/common/modals/GenericModal';
import Button from '@components/common/Button';
import { LoadingSpinner } from '@components/common/loading';
import FileUploadZone from '@components/features/import/FileUploadZone';
import { importService } from '@services';
import { showToast } from '@utils/toast';
import type { ImportType, ValidateImportBackendResponse } from '@app-types/import';
import styles from './UploadCorrectedFileModal.module.css';

interface UploadCorrectedFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  importId: number;
  importType: ImportType;
  onSuccess: () => void;
}

type ModalState = 'idle' | 'validating' | 'validated' | 'importing';

interface ValidationResult {
  totalRows: number;
  validCount: number;
  errorCount: number;
  hasErrors: boolean;
  rowErrors: RowError[];
}

interface RowError {
  rowNumber: number;
  field: string;
  message: string;
}

function extractRowErrors(response: ValidateImportBackendResponse): RowError[] {
  const errors: RowError[] = [];

  for (const row of response.rows) {
    if (!row.errors || row.errors.length === 0) continue;

    for (const err of row.errors) {
      errors.push({
        rowNumber: row.rowNumber,
        field: err.field,
        message: err.message,
      });
    }
  }

  return errors;
}

function buildValidationResult(response: ValidateImportBackendResponse): ValidationResult {
  const errorCount = response.validation?.errorCount ?? 0;
  const validCount = response.validation?.validCount ?? response.totalRows;
  const rowErrors = extractRowErrors(response);

  return {
    totalRows: response.totalRows,
    validCount,
    errorCount,
    hasErrors: errorCount > 0,
    rowErrors,
  };
}

const UploadCorrectedFileModal: React.FC<UploadCorrectedFileModalProps> = ({
  isOpen,
  onClose,
  importId,
  importType,
  onSuccess,
}) => {
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const resetState = useCallback(() => {
    setModalState('idle');
    setSelectedFile(null);
    setUploadError('');
    setValidationResult(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    setUploadError('');
    setModalState('validating');
    setValidationResult(null);

    try {
      const response = await importService.validateImportFile(file, importType);
      const result = buildValidationResult(response);
      setValidationResult(result);
      setModalState('validated');
    } catch {
      setUploadError('Failed to validate file. Please try again.');
      setModalState('idle');
      setSelectedFile(null);
    }
  }, [importType]);

  const handleFileRemove = useCallback(() => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadError('');
    setModalState('idle');
  }, []);

  const handleValidationError = useCallback((error: string) => {
    setUploadError(error);
  }, []);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setModalState('importing');
    try {
      await importService.rerunImport(importId, selectedFile);
      showToast.success('Import completed successfully');
      handleClose();
      onSuccess();
    } catch {
      showToast.error('Failed to import file');
      setModalState('validated');
    }
  }, [selectedFile, importId, handleClose, onSuccess]);

  const canImport = modalState === 'validated'
    && validationResult !== null
    && !validationResult.hasErrors
    && selectedFile !== null;

  const renderValidationResult = () => {
    if (!validationResult) return null;

    return (
      <div className={styles.validationResult}>
        <div className={validationResult.hasErrors ? styles.validationError : styles.validationSuccess}>
          <div className={styles.validationHeader}>
            {validationResult.hasErrors
              ? <><XCircle size={16} /> Validation failed</>
              : <><CheckCircle size={16} /> Validation passed</>
            }
          </div>
        </div>

        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardValue}>{validationResult.totalRows}</span>
            <span className={styles.summaryCardLabel}>Total Rows</span>
          </div>
          <div className={styles.summaryCardSuccess}>
            <span className={styles.summaryCardValue}>{validationResult.validCount}</span>
            <span className={styles.summaryCardLabel}>Valid</span>
          </div>
          <div className={styles.summaryCardError}>
            <span className={styles.summaryCardValue}>{validationResult.errorCount}</span>
            <span className={styles.summaryCardLabel}>Errors</span>
          </div>
        </div>

        {validationResult.hasErrors && validationResult.rowErrors.length > 0 && (
          <div className={styles.errorList}>
            {validationResult.rowErrors.map((err, idx) => (
              <div key={`${err.rowNumber}-${err.field}-${idx}`} className={styles.errorItem}>
                <span className={styles.errorRow}>Row {err.rowNumber}</span>
                <span className={styles.errorField}>{err.field}</span>
                <span className={styles.errorMessage}>{err.message}</span>
              </div>
            ))}
          </div>
        )}

        {validationResult.hasErrors && (
          <div className={styles.warningBanner}>
            <AlertTriangle size={16} />
            Please fix all validation errors and upload a corrected file.
          </div>
        )}
      </div>
    );
  };

  const footer = (
    <div className={styles.footerActions}>
      <Button
        variant="secondary"
        onClick={handleClose}
        disabled={modalState === 'importing'}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleImport}
        disabled={!canImport}
        loading={modalState === 'importing'}
        loadingText="Importing..."
      >
        Import File
      </Button>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Corrected File"
      size="md"
      footer={footer}
      disableBackdropClick={modalState === 'validating' || modalState === 'importing'}
      dataTestId="upload-corrected-file-modal"
    >
      <div className={styles.uploadSection}>
        <p className={styles.uploadDescription}>
          Upload a corrected CSV file to re-run this import. The file will be validated before processing.
        </p>

        {modalState === 'validating' ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
            <span className={styles.loadingText}>Validating file...</span>
          </div>
        ) : (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            selectedFile={selectedFile}
            error={uploadError}
            isProcessing={modalState === 'importing'}
            onValidationError={handleValidationError}
            title="Drop corrected CSV file here"
          />
        )}

        {renderValidationResult()}
      </div>
    </GenericModal>
  );
};

export { UploadCorrectedFileModal };
